import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, ShoppingBag, Star, Store } from "lucide-react";
import { getUniverse } from "../lib/api";
import { useCartStore } from "../store/cartStore";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const GOLD = "#C9A227";

// Univers de marque — code couleur FIXE (ne pas changer) : jaune resto, bleu kiosk, vert services.
const UNIVERSES = {
  restaurant: { title: "Restaurant & Boisson", accent: "#E8B10A", hint: "La carte de La BASE Maison" },
  kiosk: { title: "Kiosk & Bazar", accent: "#2563EB", hint: "Dépannage & bazar" },
  services: { title: "Soins & Services", accent: "#0E9F6E", hint: "Soins, services & artisans" },
};

export default function UniverseCatalog() {
  const { key } = useParams();
  const nav = useNavigate();
  const uni = UNIVERSES[key] || { title: "La BASE Maison", accent: GREEN, hint: "" };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef(null);
  const panelRefs = useRef([]);

  const addItem = useCartStore((s) => s.addItem);
  const cartCount = useCartStore((s) => s.getItemCount());

  const load = () => {
    setLoading(true);
    setError(null);
    getUniverse(key)
      .then((d) => { setData(d); setActiveIdx(0); })
      .catch(() => setError("Impossible de charger ce catalogue."))
      .finally(() => setLoading(false));
  };
  useEffect(load, [key]);

  const cats = (data?.maison?.categories || []).filter((c) => (c.items || []).length > 0);
  const partners = data?.partners || [];

  const handleAdd = (item) => {
    addItem({
      id: `rest_${item.id}`, title: item.name, price: item.price, image_url: item.image_url || undefined,
      promoKind: item.promo_active ? item.promo_kind : null,
      originalPrice: item.original_price,
      promoLabel: item.promo_active ? item.promo_label : null,
    });
    toast.success(`${item.name} ajouté au panier`);
  };

  const goTo = (i) => {
    setActiveIdx(i);
    const el = panelRefs.current[i];
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };
  const onScroll = () => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const idx = Math.round(sc.scrollLeft / sc.clientWidth);
    if (idx !== activeIdx) setActiveIdx(idx);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="universe-page">
      <button onClick={() => nav("/")} data-testid="uni-back"
        className="flex items-center gap-1 text-sm font-bold mb-4" style={{ color: GREEN }}>
        <ArrowLeft size={18} /> Accueil
      </button>

      {/* Hero univers */}
      <div className="rounded-3xl p-6 mb-6 text-white" style={{ background: uni.accent }} data-testid="uni-hero">
        <p className="text-xs font-bold uppercase opacity-80">La BASE Maison</p>
        <h1 className="text-3xl font-extrabold">{uni.title}</h1>
        {uni.hint && <p className="text-sm opacity-90 mt-1">{uni.hint}</p>}
      </div>

      {key === "services" && (
        <button data-testid="uni-parcel-cta" onClick={() => nav("/services/colis")}
          className="w-full rounded-2xl p-4 mb-6 flex items-center gap-3 text-left" style={{ background: "#eafff6", border: `1px solid ${GREEN}` }}>
          <span style={{ fontSize: 26 }}>📦</span>
          <span><span className="block font-extrabold" style={{ color: GREEN }}>Livraison colis & courrier</span>
          <span className="block text-sm" style={{ color: "#64748b" }}>Express ou programmé · livreurs La BASE ou entre particuliers</span></span>
        </button>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="uni-loading">
          {[...Array(6)].map((_, i) => <div key={i} className="h-56 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} />)}
        </div>
      ) : error ? (
        <div className="text-center py-10">
          <p style={{ color: "#64748b" }}>{error}</p>
          <button data-testid="uni-retry" onClick={load} className="mt-4 rounded-xl font-bold text-white px-5 py-2" style={{ background: GREEN }}>Réessayer</button>
        </div>
      ) : (
        <>
          {/* Sous-rubriques SWIPE (La BASE Maison) */}
          {cats.length > 0 ? (
            <>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="uni-subcats">
                {cats.map((c, i) => (
                  <button key={c.id} data-testid={`uni-subcat-${c.id}`} onClick={() => goTo(i)}
                    className="px-4 py-2 rounded-full text-sm font-semibold flex-shrink-0 transition-colors"
                    style={activeIdx === i
                      ? { background: uni.accent, color: "#fff" }
                      : { background: "#f5f7f6", color: "#1f2937", border: "1px solid #e5e7eb" }}>
                    {c.name}
                  </button>
                ))}
              </div>

              <div ref={scrollerRef} onScroll={onScroll}
                className="flex overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-24"
                style={{ scrollbarWidth: "none" }} data-testid="uni-swiper">
                {cats.map((c, i) => (
                  <div key={c.id} ref={(el) => (panelRefs.current[i] = el)}
                    className="snap-start shrink-0 w-full pr-2" data-testid={`uni-panel-${c.id}`}>
                    <h2 className="text-xl font-extrabold mb-3" style={{ color: GREEN }}>{c.name}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {c.items.map((item) => {
                        const unavailable = item.is_available === false;
                        return (
                          <div key={item.id} data-testid={`uni-item-${item.id}`}
                            className="rounded-2xl overflow-hidden bg-white flex flex-col" style={{ border: "1px solid #f0f0f0" }}>
                            <div className="relative h-36 flex items-center justify-center" style={{ background: "#f5f7f6" }}>
                              {item.image_url
                                ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                : <ShoppingBag size={34} color="#cbd5e1" />}
                              {item.promo_active && (
                                <span data-testid={`uni-promo-${item.id}`} className="absolute left-3 top-3 text-[11px] font-extrabold px-2 py-1 rounded-full text-white" style={{ background: ORANGE }}>
                                  {item.promo_label || `-${Number(item.promo?.percent || 0).toFixed(0)}%`}
                                </span>
                              )}
                              {item.recommended && (
                                <span className="absolute right-3 top-3 inline-flex items-center gap-1 text-[11px] font-extrabold px-2 py-1 rounded-full" style={{ background: "#fffdf5", color: GOLD, border: `1px solid ${GOLD}` }}>
                                  <Star size={11} /> Reco
                                </span>
                              )}
                              <button data-testid={`uni-add-${item.id}`} disabled={unavailable} onClick={() => handleAdd(item)}
                                className="absolute right-3 bottom-3 w-11 h-11 rounded-full flex items-center justify-center text-white"
                                style={{ background: unavailable ? "#9ca3af" : GREEN, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
                                {unavailable ? <X size={22} /> : <Plus size={22} />}
                              </button>
                            </div>
                            <div className="p-3">
                              <p className="font-bold truncate" style={{ color: "#1f2937" }}>{item.name}</p>
                              {item.promo_active && Number(item.effective_price ?? item.price) < Number(item.original_price ?? item.price) ? (
                                <p className="font-extrabold flex items-center gap-2">
                                  <span style={{ textDecoration: "line-through", color: "#94a3b8" }}>CHF {Number(item.original_price ?? item.price).toFixed(2)}</span>
                                  <span style={{ color: ORANGE }}>CHF {Number(item.price).toFixed(2)}</span>
                                </p>
                              ) : (
                                <p className="font-extrabold" style={{ color: ORANGE }}>CHF {Number(item.price).toFixed(2)}</p>
                              )}
                              {item.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: "#94a3b8" }}>{item.description}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-10 rounded-2xl mb-6" style={{ background: "#f8fafc" }} data-testid="uni-maison-empty">
              <p style={{ color: "#64748b" }}>Le catalogue La BASE Maison de cet univers arrive bientôt.</p>
            </div>
          )}

          {/* Partenaires */}
          {partners.length > 0 && (
            <div className="mt-2" data-testid="uni-partners">
              <h2 className="text-xl font-extrabold mb-3" style={{ color: GREEN }}>Nos partenaires</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pb-24">
                {partners.map((r) => (
                  <button key={r.id} data-testid={`uni-partner-${r.id}`} onClick={() => nav(`/restaurant/${r.id}`)}
                    className="rounded-2xl overflow-hidden bg-white text-left flex flex-col" style={{ border: "1px solid #f0f0f0" }}>
                    <div className="h-28 flex items-center justify-center" style={{ background: "#f5f7f6" }}>
                      {r.hero_image_url ? <img src={r.hero_image_url} alt={r.name} className="w-full h-full object-cover" /> : <Store size={30} color="#cbd5e1" />}
                    </div>
                    <div className="p-3">
                      <p className="font-bold truncate" style={{ color: "#1f2937" }}>{r.name}</p>
                      {r.cuisine && <p className="text-xs" style={{ color: "#94a3b8" }}>{r.cuisine}</p>}
                      {r.eta_label && <p className="text-[11px] mt-1" style={{ color: GREEN }}>{r.eta_label}</p>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {cartCount > 0 && (
        <button data-testid="uni-view-cart" onClick={() => nav("/panier")}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-2xl text-white font-bold px-6 py-3 flex items-center gap-2 z-50"
          style={{ background: GREEN, boxShadow: "0 6px 18px rgba(0,0,0,0.25)" }}>
          <ShoppingBag size={20} /> Voir le panier ({cartCount})
        </button>
      )}
    </div>
  );
}
